const SVG_TAG_REGEX = /<svg\b([^>]*)>/i;

const attrRegex = (name) =>
    new RegExp(`\\b${name}=(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');

const getAttrValue = (attrs, name) => {
    const match = String(attrs || '').match(attrRegex(name));
    return match ? match[1] || match[2] || match[3] || '' : '';
};

const replaceOrAddAttr = (attrs, name, value) => {
    const pattern = new RegExp(`\\s${name}=("[^"]*"|'[^']*'|[^\\s>]+)`, 'i');
    if (pattern.test(attrs)) {
        return attrs.replace(pattern, ` ${name}="${value}"`);
    }
    return `${attrs} ${name}="${value}"`;
};

const parseNumericDimension = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);
    if (/^\d+(\.\d+)?px$/i.test(raw)) return Number(raw.replace(/px$/i, ''));
    return null;
};

const parseViewBox = (value) => {
    const parts = String(value || '')
        .trim()
        .split(/[\s,]+/)
        .map(Number);
    if (parts.length !== 4 || parts.some((part) => Number.isNaN(part))) {
        return null;
    }
    const [, , width, height] = parts;
    if (width <= 0 || height <= 0) return null;
    return { width, height };
};

const DEFAULT_SVG_DIMENSION = 512;

const isSvgUploadFile = (file) => {
    if (!file || typeof file.text !== 'function') return false;
    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    return type === 'image/svg+xml' || name.endsWith('.svg');
};

export const normalizeSvgMarkupForUpload = (markup) => {
    if (typeof markup !== 'string' || !SVG_TAG_REGEX.test(markup)) {
        return markup;
    }

    return markup.replace(SVG_TAG_REGEX, (match, attrs = '') => {
        let next = attrs || '';
        const widthAttr = getAttrValue(next, 'width');
        const heightAttr = getAttrValue(next, 'height');
        const viewBoxAttr = getAttrValue(next, 'viewBox');
        const widthValue = parseNumericDimension(widthAttr);
        const heightValue = parseNumericDimension(heightAttr);
        const viewBox = parseViewBox(viewBoxAttr);

        const resolvedWidth =
            widthValue || viewBox?.width || DEFAULT_SVG_DIMENSION;
        const resolvedHeight =
            heightValue || viewBox?.height || DEFAULT_SVG_DIMENSION;

        if (!widthValue) {
            next = replaceOrAddAttr(next, 'width', `${resolvedWidth}`);
        }
        if (!heightValue) {
            next = replaceOrAddAttr(next, 'height', `${resolvedHeight}`);
        }
        if (!viewBoxAttr) {
            next = replaceOrAddAttr(
                next,
                'viewBox',
                `0 0 ${resolvedWidth} ${resolvedHeight}`
            );
        }

        return `<svg${next}>`;
    });
};

export const normalizeSvgFileForUpload = async (file) => {
    if (!isSvgUploadFile(file) || typeof File !== 'function') {
        return file;
    }

    try {
        const markup = await file.text();
        const normalizedMarkup = normalizeSvgMarkupForUpload(markup);
        if (!normalizedMarkup || normalizedMarkup === markup) {
            return file;
        }

        return new File([normalizedMarkup], file.name, {
            type: file.type || 'image/svg+xml',
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    }
};
